import type { Request } from '@adornis/abac/core/Request.js';
import { ERMAction, type IERMPayload, initERM, isERMPayload } from '@adornis/entity-relationship-management/init.js';
import { accessController } from '@saraa/permissions/abac.js';
import { Organizations, TemplateFields, Templates, Users } from '@saraa/permissions/policies/index.js';
import { PAGE_CHECK } from '@saraa/permissions/policyHelper.js';

// biome-ignore lint/suspicious/noExplicitAny: the scopes differ in their data shapes
type ScopeRequirement = { createPayload: (payload: any) => any; action: string };

const requires = (
  // biome-ignore lint/suspicious/noExplicitAny: as above
  scope: { createPayload: (payload: any) => any },
  action: string,
): ScopeRequirement => ({ createPayload: scope.createPayload, action });

/**
 * Which scope actions a route needs, keyed by route name.
 *
 * **Every entity route needs two entries.** ERM checks route visibility through
 * two different code paths that spell the route differently:
 *
 *   - the sidebar uses `entityDef.route ?? entityDef._class._class` → `'templates'`
 *   - the page guard uses `route.metaData?.name ?? route.label`, and entity routes
 *     are registered with `label: entityDef._class._class` → `'Template'`
 *
 * Miss one and you get a route that is hidden but reachable by URL, or visible
 * but forbidden when clicked. Admin pages diverge the same way: the sidebar uses
 * `AdminPagePaths[key]` while the page guard uses the route label.
 *
 * Admin routes deliberately require a *write* action rather than `view`: an
 * ORG_USER may view templates (they need them on the generate page) but must not
 * reach the admin area.
 */
const TEMPLATES_ROUTE = [requires(Templates, 'modify')];
const TEMPLATE_FIELDS_ROUTE = [requires(TemplateFields, 'modify')];
// An ORG_ADMIN may edit the organizations they administer.
const ORGANIZATIONS_ROUTE = [requires(Organizations, 'modify')];
const USERS_ROUTE = [requires(Users, 'view')];

const routePolicyMap: Record<string, ScopeRequirement[]> = {
  // Templates
  templates: TEMPLATES_ROUTE,
  Template: TEMPLATES_ROUTE,

  // Template fields
  'template-fields': TEMPLATE_FIELDS_ROUTE,
  TemplateField: TEMPLATE_FIELDS_ROUTE,

  // Organizations
  organizations: ORGANIZATIONS_ROUTE,
  Organization: ORGANIZATIONS_ROUTE,

  // Admin pages: sidebar path, then page-guard label.
  users: USERS_ROUTE,
  'user-management': USERS_ROUTE,
  // There is deliberately no `roles` / `role-management` entry — the page is not
  // configured, so the route does not exist, and the default-deny below is the
  // backstop if anything ever tries to reach it by URL.
};

/** Every route the admin area is made of, deduplicated across the two spellings. */
const ADMIN_AREA_REQUIREMENTS = [...TEMPLATES_ROUTE, ...TEMPLATE_FIELDS_ROUTE, ...ORGANIZATIONS_ROUTE, ...USERS_ROUTE];

/** Entity class name → the scope governing it, for the CRUD gates. */
// biome-ignore lint/suspicious/noExplicitAny: as above
const entityScopeMap: Record<string, { createPayload: (payload: any) => any }> = {
  Template: Templates,
  TemplateField: TemplateFields,
  Organization: Organizations,
  AdornisUser: Users,
  SaraaUser: Users,
};

const ERM_ACTION_TO_SCOPE_ACTION: Partial<Record<ERMAction, string>> = {
  [ERMAction.CreateEntity]: 'create',
  [ERMAction.UpdateEntity]: 'modify',
  [ERMAction.DeleteEntity]: 'delete',
  [ERMAction.ArchiveEntity]: 'delete',
  [ERMAction.UnarchiveEntity]: 'delete',
  [ERMAction.ViewEntity]: 'view',
};

const warnedRoutes = new Set<string>();

const evaluateAny = async (requirements: ScopeRequirement[], describe: string) => {
  for (const { createPayload, action } of requirements) {
    const result = await accessController.evaluate(createPayload({ action, data: PAGE_CHECK }));
    if (result.allowed) return { allowed: true, reason: `${describe} granted via ${action}` };
  }
  return { allowed: false, reason: `no permission for ${describe}` };
};

/**
 * Whether the admin area is worth offering this user at all — true as soon as one
 * of its routes would let them in.
 *
 * Read by the generate page to decide whether to render its Admin button, so that
 * an ORG_USER is not sent to a shell whose every page then refuses them. It
 * evaluates the very requirements the route guards do, rather than testing
 * `isPlatformAdmin` or a membership role directly: the button then cannot drift
 * away from what the routes actually allow.
 */
export const canReachAdminArea = async () => {
  const { allowed } = await evaluateAny(ADMIN_AREA_REQUIREMENTS, 'the admin area');
  return allowed;
};

const evaluateRoute = async (payload: IERMPayload) => {
  const route = payload.route;
  if (!route) return { allowed: false, reason: 'route check without a route' };

  const requirements = routePolicyMap[route];
  if (!requirements) {
    // Default-deny. A new entity must not become world-readable just because
    // someone forgot to add it here — but make that mistake loud.
    if (!warnedRoutes.has(route)) {
      warnedRoutes.add(route);
      console.warn(`[saraa] no route policy for "${route}" — denying. Add it to routePolicyMap in client/erm.ts.`);
    }
    return { allowed: false, reason: `no route policy for ${route}` };
  }

  return evaluateAny(requirements, `route ${route}`);
};

const evaluateEntityAction = async (payload: IERMPayload) => {
  const scopeAction = ERM_ACTION_TO_SCOPE_ACTION[payload.action];
  const scope = payload.entityClass ? entityScopeMap[payload.entityClass] : undefined;
  if (!scopeAction || !scope) {
    return { allowed: false, reason: `no policy for ${payload.action} on ${payload.entityClass ?? 'unknown'}` };
  }

  /**
   * ERM's CRUD gates carry no entity data — `generic-entity-manager` sends only
   * `{action, entityClass, entityID, userID}`. So this can only answer the coarse
   * question "could this user ever do this, in any organization", which is fine
   * because these gates just show or hide buttons. The per-row decision is made
   * server-side, by the `abacGuard` on the endpoint the button calls.
   */
  return evaluateAny(
    [{ createPayload: scope.createPayload, action: scopeAction }],
    `${payload.action} on ${payload.entityClass}`,
  );
};

export const SaraaERMPolicy = {
  name: 'SaraaERMPolicy',

  async evaluate(request: Request) {
    if (!isERMPayload(request.payload)) return false;
    const payload = request.payload;

    // Tags and attached files are not part of saraa.
    if ([ERMAction.AddFile, ERMAction.RemoveFile, ERMAction.AddTag, ERMAction.RemoveTag].includes(payload.action)) {
      return { allowed: false, reason: 'tags and files are not activated for saraa' };
    }

    if (payload.action === ERMAction.ViewRoute) return evaluateRoute(payload);

    return evaluateEntityAction(payload);
  },
};

export const initSaraaErm = () => {
  initERM({ policies: [SaraaERMPolicy] });
};
